import React, { useState, useEffect } from 'react';
import { X, Download, Edit2 } from 'lucide-react';
import QRCode from 'qrcode';
import { BASE_URL } from '../../utils/api';

function formatNumber(num) {
  const n = parseFloat(num) || 0;
  return n.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatDate(dateInput) {
  if (!dateInput) return '-';
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

const InvoiceViewModal = ({ isOpen, onClose, invoice, onEdit }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [directPdfUrl, setDirectPdfUrl] = useState('');

  useEffect(() => {
    if (invoice) {
      let hostUrl = BASE_URL;
      if (typeof window !== 'undefined' && window.location.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        hostUrl = 'https://api-gateway.abhayagencies.com';
      } else if (invoice.publicBaseUrl) {
        hostUrl = invoice.publicBaseUrl;
      }
      const cleanBase = (hostUrl || 'https://api-gateway.abhayagencies.com').replace(/\/$/, '');
      const qrUrl = `${cleanBase}/v1/proforma-invoices/public/${invoice.id || invoice.invoiceNumber}/pdf`;
      setDirectPdfUrl(qrUrl);
      QRCode.toDataURL(qrUrl, { margin: 1, width: 140, errorCorrectionLevel: 'M' })
        .then(url => setQrCodeUrl(url))
        .catch(err => console.error('Failed to generate QR code in view modal:', err));
    }
  }, [invoice]);

  if (!isOpen || !invoice) return null;

  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const items = invoice.items || [];

  const handleDownloadPdf = () => {
    const downloadUrl = `${BASE_URL}/v1/proforma-invoices/${invoice.id}/pdf?download=true&token=${token}`;
    window.open(downloadUrl, '_blank');
  };

  const isInterState = invoice.igstTotal > 0 || (
    invoice.customerState && invoice.agencyState &&
    invoice.customerState.toLowerCase() !== invoice.agencyState.toLowerCase()
  );

  const rawWords = (invoice.totalInWords || '')
    .replace(/^Indian Rupees\s*/i, '')
    .replace(/\s*Only$/i, '')
    .trim();
  const wordsFormatted = rawWords ? `(${rawWords} & Rupees Only)` : '(Zero Rupees Only)';

  const displayBranch = (invoice.branchName && invoice.branchName.toLowerCase().includes('surat'))
    ? 'SURAT'
    : (invoice.branchName || 'SURAT');

  const shipName = invoice.isSameAsBilling ? invoice.customerName : (invoice.shippingName || invoice.customerName);
  const shipStreet = invoice.isSameAsBilling ? invoice.billingStreet : (invoice.shippingStreet || invoice.billingStreet);
  const shipCityState = invoice.isSameAsBilling
    ? [invoice.billingCity, invoice.billingState, invoice.billingPincode].filter(Boolean).join(', ')
    : [invoice.shippingCity, invoice.shippingState, invoice.shippingPincode].filter(Boolean).join(', ');

  const custCityState = [invoice.billingCity, invoice.billingState, invoice.billingPincode].filter(Boolean).join(', ');

  const summaryGstRate = items[0]?.gstRate || 18;
  const summaryHalfRate = summaryGstRate / 2;

  return (
    <div className="modal-backdrop" onClick={onClose} style={{ zIndex: 10000, overflowY: 'auto', padding: '1.5rem 1rem' }}>
      <div
        className="modal-content fade-in"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '880px',
          width: '100%',
          maxHeight: '96vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
        }}
      >
        {/* Top Control Bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.8rem 1.2rem',
          backgroundColor: '#0f172a',
          color: '#ffffff',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#ffffff' }}>
              Proforma Invoice &mdash; {invoice.invoiceNumber}
            </span>
            <span style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: '9999px',
              backgroundColor: invoice.status === 'Approved' ? '#dcfce7' : invoice.status === 'Sent' ? '#dbeafe' : '#fef3c7',
              color: invoice.status === 'Approved' ? '#15803d' : invoice.status === 'Sent' ? '#1e40af' : '#92400e',
              textTransform: 'uppercase'
            }}>
              {invoice.status || 'Draft'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={handleDownloadPdf}
              className="btn btn-primary"
              style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', gap: '5px' }}
              title="Download Vector PDF"
            >
              <Download size={15} /> PDF
            </button>
            {onEdit && (
              <button
                onClick={() => { onClose(); onEdit(invoice); }}
                className="btn"
                style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', gap: '5px', background: '#e2e8f0', color: '#1e293b' }}
              >
                <Edit2 size={15} /> Edit
              </button>
            )}
            <button
              onClick={onClose}
              style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', marginLeft: '6px' }}
            >
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Paper Document Preview (Exact Mirror of Paper / PDF) */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '1.5rem 2rem', backgroundColor: '#f8fafc' }}>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: '#ffffff',
            padding: '20px 24px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            fontFamily: 'Arial, Helvetica, sans-serif',
            fontSize: '11px',
            color: '#000000',
            lineHeight: 1.35
          }}>
            {/* Title */}
            <div style={{ textAlign: 'center', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
              Proforma Invoice
            </div>

            {/* Main Outer Box */}
            <div style={{ border: '1.2px solid #222' }}>

              {/* 1. Header Compartments */}
              <div style={{ display: 'grid', gridTemplateColumns: '50% 1fr 85px', borderBottom: '1.2px solid #222' }}>
                {/* Box 1: Logo & Agency Details (Side-by-side, top-aligned, no divider) */}
                <div style={{ padding: '6px 8px', borderRight: '1px solid #222', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <img
                    src="/logo.png"
                    alt="Logo"
                    onError={(e) => {
                      if (invoice.agencyLogo && e.target.src !== `${BASE_URL}/${invoice.agencyLogo}`) {
                        e.target.src = `${BASE_URL}/${invoice.agencyLogo.replace(/^\//, '')}`;
                      }
                    }}
                    style={{ maxWidth: '85px', maxHeight: '80px', objectFit: 'contain', flexShrink: 0, marginTop: '1px' }}
                  />
                  <div style={{ fontSize: '9.5px', lineHeight: 1.35, flex: 1 }}>
                    <div style={{ fontSize: '11.5px', fontWeight: 'bold', color: '#000000', marginBottom: '2px' }}>
                      {invoice.agencyName || 'Abhay Agency'}
                    </div>
                    {invoice.agencyAddress && (
                      <div>{invoice.agencyAddress}</div>
                    )}
                    {([invoice.agencyCity, invoice.agencyPincode].filter(Boolean).length > 0) && (
                      <div>{[invoice.agencyCity, invoice.agencyPincode].filter(Boolean).join('-')}</div>
                    )}
                    {[
                      ...(invoice.agencyPhone ? invoice.agencyPhone.split(',') : []),
                      ...(invoice.agencyPhone2 ? invoice.agencyPhone2.split(',') : [])
                    ].map(p => p.trim()).filter(Boolean).map((ph, idx) => (
                      <div key={idx}>Ph No: {ph}</div>
                    ))}
                    {invoice.agencyEmail && (
                      <div>Email ID: {invoice.agencyEmail}</div>
                    )}
                  </div>
                </div>

                {/* Box 3: Tax Meta & Recipient Badge */}
                <div style={{ padding: '6px 8px', borderRight: '1px solid #222', fontSize: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                    <span style={{ border: '1px solid #777', fontSize: '8.5px', padding: '2px 8px', color: '#333' }}>
                      Original for Recipient
                    </span>
                  </div>
                  <div style={{ lineHeight: 1.6, textAlign: 'right' }}>
                    <div><strong>State Name/Code:</strong> {invoice.agencyState || 'Gujarat'}-{invoice.agencyStateCode || (invoice.agencyGst && invoice.agencyGst.length >= 2 ? invoice.agencyGst.substring(0, 2) : '24')}</div>
                    <div><strong>GST NO:</strong> {invoice.agencyGst || '-'}</div>
                    <div><strong>PAN NO:</strong> {invoice.agencyPan || (invoice.agencyGst && invoice.agencyGst.length >= 12 ? invoice.agencyGst.substring(2, 12) : '-')}</div>
                  </div>
                </div>

                {/* Box 4: QR Code */}
                <div style={{ padding: '6px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {qrCodeUrl ? (
                    <a href={directPdfUrl} target="_blank" rel="noopener noreferrer" title="Click or Scan to view/download PDF">
                      <img src={qrCodeUrl} alt="QR Code" style={{ width: '70px', height: '70px', display: 'block', cursor: 'pointer' }} />
                    </a>
                  ) : (
                    <div style={{ width: '70px', height: '70px', background: '#f1f5f9' }} />
                  )}
                </div>
              </div>

              {/* 2. Meta Bar */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '5px 8px',
                fontSize: '11px',
                fontWeight: 'bold',
                borderBottom: '1.2px solid #222'
              }}>
                <div>Invoice No: {invoice.invoiceNumber || 'P2061'}</div>
                <div>Invoice Date: {formatDate(invoice.invoiceDate)}</div>
              </div>

              {/* 3. Addresses Section */}
              <div style={{ display: 'grid', gridTemplateColumns: '50% 50%', borderBottom: '1.2px solid #222', minHeight: '75px' }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #222', fontSize: '9.5px', lineHeight: 1.35 }}>
                  <div style={{ fontSize: '10px', fontWeight: 'bold', textDecoration: 'underline', marginBottom: '4px' }}>
                    Billing Address
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 'bold' }}>{invoice.customerName || 'Customer'}</div>
                  {invoice.billingStreet && <div>{invoice.billingStreet}</div>}
                  {custCityState && <div>{custCityState}</div>}
                  <div>GST No: {invoice.customerGst || '-'}</div>
                </div>

                <div style={{ padding: '6px 8px', textAlign: 'right', fontSize: '9.5px', lineHeight: 1.35 }}>
                  <div style={{ fontSize: '10px', fontWeight: 'bold', textDecoration: 'underline', marginBottom: '4px' }}>
                    Place of Supply of Service
                  </div>
                  <div style={{ fontSize: '11px', fontWeight: 'bold' }}>{shipName || 'Customer'}</div>
                  {shipStreet && <div>{shipStreet}</div>}
                  {shipCityState && <div>{shipCityState}</div>}
                </div>
              </div>

              {/* 4. Line Items Table */}
              <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '9px' }}>
                <colgroup>
                  <col style={{ width: '4%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '11%' }} />
                  {isInterState ? (
                    <>
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '17%' }} />
                    </>
                  ) : (
                    <>
                      <col style={{ width: '4%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '4%' }} />
                      <col style={{ width: '8%' }} />
                    </>
                  )}
                  <col style={{ width: '10%' }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    <th rowSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px', textAlign: 'center' }}>Sl<br />No</th>
                    <th rowSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px 6px', textAlign: 'left' }}>Product Code</th>
                    <th rowSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px 6px', textAlign: 'left' }}>Item/Product Description</th>
                    <th rowSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px', textAlign: 'center' }}>SAC/ HSN<br />Code</th>
                    <th rowSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px', textAlign: 'center' }}>Qty</th>
                    <th rowSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px 6px', textAlign: 'right' }}>Amount</th>
                    {isInterState ? (
                      <th colSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px', textAlign: 'center' }}>IGST</th>
                    ) : (
                      <>
                        <th colSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px', textAlign: 'center' }}>SGST</th>
                        <th colSpan={2} style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '4px', textAlign: 'center' }}>CGST</th>
                      </>
                    )}
                    <th rowSpan={2} style={{ borderBottom: '1px solid #222', padding: '4px 6px', textAlign: 'right' }}>Total</th>
                  </tr>
                  <tr style={{ background: '#fafafa' }}>
                    {isInterState ? (
                      <>
                        <th style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '3px', textAlign: 'center' }}>%</th>
                        <th style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '3px 6px', textAlign: 'right' }}>Tax Amt</th>
                      </>
                    ) : (
                      <>
                        <th style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '3px', textAlign: 'center' }}>%</th>
                        <th style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '3px 4px', textAlign: 'right' }}>Tax Amt</th>
                        <th style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '3px', textAlign: 'center' }}>%</th>
                        <th style={{ borderRight: '1px solid #222', borderBottom: '1px solid #222', padding: '3px 4px', textAlign: 'right' }}>Tax Amt</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const itemTax = (item.totalAmount || 0) - (item.taxableAmount || 0);
                    const halfTax = itemTax / 2;
                    const halfRate = (item.gstRate || 0) / 2;

                    return (
                      <tr key={idx} style={{ height: '20px' }}>
                        <td style={{ borderRight: '1px solid #222', padding: '3px', textAlign: 'center' }}>{idx + 1}</td>
                        <td style={{ borderRight: '1px solid #222', padding: '3px 6px' }}>{item.productCode || item.hsnCode || 'WC95Z18212'}</td>
                        <td style={{ borderRight: '1px solid #222', padding: '3px 6px', fontWeight: 'bold' }}>{item.name || '-'}</td>
                        <td style={{ borderRight: '1px solid #222', padding: '3px', textAlign: 'center' }}>{item.hsnCode || '8443'}</td>
                        <td style={{ borderRight: '1px solid #222', padding: '3px', textAlign: 'center' }}>{item.quantity || 1}</td>
                        <td style={{ borderRight: '1px solid #222', padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(item.taxableAmount || item.unitPrice)}</td>
                        {isInterState ? (
                          <>
                            <td style={{ borderRight: '1px solid #222', padding: '3px', textAlign: 'center' }}>{item.gstRate || 0}</td>
                            <td style={{ borderRight: '1px solid #222', padding: '3px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(itemTax)}</td>
                          </>
                        ) : (
                          <>
                            <td style={{ borderRight: '1px solid #222', padding: '3px', textAlign: 'center' }}>{halfRate}</td>
                            <td style={{ borderRight: '1px solid #222', padding: '3px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(halfTax)}</td>
                            <td style={{ borderRight: '1px solid #222', padding: '3px', textAlign: 'center' }}>{halfRate}</td>
                            <td style={{ borderRight: '1px solid #222', padding: '3px 4px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(halfTax)}</td>
                          </>
                        )}
                        <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{formatNumber(item.totalAmount)}</td>
                      </tr>
                    );
                  })}
                  {/* Empty filler rows ensuring grid lines extend downward */}
                  <tr style={{ height: items.length > 5 ? '30px' : '90px' }}>
                    <td style={{ borderRight: '1px solid #222' }}></td>
                    <td style={{ borderRight: '1px solid #222' }}></td>
                    <td style={{ borderRight: '1px solid #222' }}></td>
                    <td style={{ borderRight: '1px solid #222' }}></td>
                    <td style={{ borderRight: '1px solid #222' }}></td>
                    <td style={{ borderRight: '1px solid #222' }}></td>
                    {isInterState ? (
                      <>
                        <td style={{ borderRight: '1px solid #222' }}></td>
                        <td style={{ borderRight: '1px solid #222' }}></td>
                      </>
                    ) : (
                      <>
                        <td style={{ borderRight: '1px solid #222' }}></td>
                        <td style={{ borderRight: '1px solid #222' }}></td>
                        <td style={{ borderRight: '1px solid #222' }}></td>
                        <td style={{ borderRight: '1px solid #222' }}></td>
                      </>
                    )}
                    <td></td>
                  </tr>
                </tbody>
              </table>

              {/* 5. Totals & Words Section */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', borderTop: '1.2px solid #222', borderBottom: '1.2px solid #222' }}>
                <div style={{ padding: '8px 10px', borderRight: '1.2px solid #222' }}>
                  <div style={{ fontWeight: 'bold', textDecoration: 'underline', fontSize: '9.5px', marginBottom: '4px' }}>
                    Amount in Words
                  </div>
                  <div style={{ fontSize: '9.5px', fontWeight: 'bold', color: '#222', lineHeight: 1.4 }}>
                    {wordsFormatted}
                  </div>
                </div>

                <div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9.5px' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '3px 6px', borderBottom: '1px solid #222' }}>Sub Total :</td>
                        <td style={{ padding: '3px 6px', borderBottom: '1px solid #222', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(invoice.subtotal)}</td>
                      </tr>
                      {isInterState ? (
                        <>
                          <tr>
                            <td style={{ padding: '3px 6px', borderBottom: '1px solid #222' }}>IGST :</td>
                            <td style={{ padding: '3px 6px', borderBottom: '1px solid #222', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(invoice.igstTotal || 0)}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '3px 6px', borderBottom: '1px solid #222' }}>Round Off :</td>
                            <td style={{ padding: '3px 6px', borderBottom: '1px solid #222', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(invoice.roundOff || 0)}</td>
                          </tr>
                        </>
                      ) : (
                        <>
                          <tr>
                            <td style={{ padding: '3px 6px', borderBottom: '1px solid #222' }}>SGST :</td>
                            <td style={{ padding: '3px 6px', borderBottom: '1px solid #222', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(invoice.sgstTotal || 0)}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: '3px 6px', borderBottom: '1px solid #222' }}>CGST :</td>
                            <td style={{ padding: '3px 6px', borderBottom: '1px solid #222', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(invoice.cgstTotal || 0)}</td>
                          </tr>
                        </>
                      )}
                      <tr style={{ fontWeight: 'bold', background: '#fafafa' }}>
                        <td style={{ padding: '4px 6px' }}>Grand Total (Rs) :</td>
                        <td style={{ padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatNumber(invoice.grandTotal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* 6. Acknowledgement & Signatory */}
              <div style={{ display: 'grid', gridTemplateColumns: '50% 50%', borderBottom: '1.2px solid #222', minHeight: '75px' }}>
                <div style={{ padding: '6px 8px', borderRight: '1px solid #222', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '9.5px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '10px', marginBottom: '4px' }}>Acknowledgement Information</div>
                    <div>Name:</div>
                    <div style={{ marginTop: '3px' }}>Date:</div>
                    <div style={{ marginTop: '3px' }}>Contact No:</div>
                  </div>
                  <div style={{ textAlign: 'right', color: '#444', fontSize: '8.5px' }}>(Seal & Signature)</div>
                </div>

                <div style={{ padding: '6px 8px', textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '9.5px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '10px' }}>
                    {invoice.authorizedSignatoryLabel || 'For ABHAY AGENCIES'}
                  </div>
                  <div style={{ height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <img
                      src="/signature.png"
                      alt="Signature"
                      onError={(e) => {
                        if (invoice.signatureImage && e.target.src !== `${BASE_URL}/${invoice.signatureImage.replace(/^\//, '')}`) {
                          e.target.src = `${BASE_URL}/${invoice.signatureImage.replace(/^\//, '')}`;
                        } else {
                          e.target.style.display = 'none';
                        }
                      }}
                      style={{ maxHeight: '35px', maxWidth: '105px', objectFit: 'contain' }}
                    />
                  </div>
                  <div style={{ color: '#444', fontSize: '8.5px' }}>(Authorised Signatory)</div>
                </div>
              </div>

              {/* 7. Footer Strip */}
              <div style={{ textAlign: 'center', padding: '4px', fontSize: '9px', color: '#333' }}>
                This Invoice is Computer Generated
              </div>
            </div>

            {/* 8. Bank Details Outside Box */}
            {(invoice.showBankDetails !== false && invoice.showBankDetails !== 'false' && Boolean(invoice.showBankDetails)) && (
              <div style={{ marginTop: '10px', fontSize: '10px', lineHeight: 1.45, color: '#000000' }}>
                <div>Company Name: {invoice.accountName || invoice.agencyName || 'Abhay Agency'}</div>
                <div>Bank Name: {invoice.bankName || 'State Bank of India'}</div>
                <div>A/C No: {invoice.accountNumber || '-'},</div>
                <div>A/C Type: {invoice.accountType || 'Current Account'}</div>
                <div>Branch: {invoice.branchName || invoice.agencyCity || 'SURAT'},</div>
                <div>IFSC/RTGS Code: {invoice.ifscCode || '-'}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceViewModal;
